/**
 * Produse — catalogul central al restaurantului.
 *
 * Aici se schimbă o singură dată ceea ce apare în toate canalele (QR, meniu web, ecrane).
 * Pagina este optimizată pentru operațiunea reală din restaurant: căutare instant, filtre,
 * comutare de disponibilitate cu un click („s-a terminat pizza diavola”) și editare rapidă.
 */
import { useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';
import { api, uploadFile } from '../lib/api.js';
import { formatCurrency, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

interface Product {
  id: string;
  public_id: string;
  name: string;
  description: string | null;
  short_description: string | null;
  price_cents: number;
  compare_at_price_cents: number | null;
  currency: string;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  restaurant_id: string;
  restaurant_name: string;
  is_available: boolean;
  is_featured: boolean;
  is_new: boolean;
  is_vegetarian: boolean;
  is_vegan: boolean;
  is_gluten_free: boolean;
  is_alcoholic: boolean;
  calories_kcal: number | null;
  weight_grams: number | null;
  prep_time_minutes: number | null;
  spiciness: number;
  /** Imaginea principală + galeria, exact cum vin din API (prima este cea afișată public). */
  images: Array<{ id: string; assetId: string; publicId: string; role: 'primary' | 'gallery'; alt: string | null }>;
}

interface Category {
  id: string;
  name: string;
  restaurant_id: string;
  products: number;
}

interface Restaurant {
  id: string;
  name: string;
  currency: string;
}

interface CatalogMenu {
  id: string;
  name: string;
  restaurant_id: string;
  status: string;
  ordering_mode: 'view_only' | 'dine_in' | 'online';
  items: number;
}

const MENU_MODE: Record<CatalogMenu['ordering_mode'], string> = {
  view_only: 'doar citire',
  dine_in: 'la masă',
  online: 'online',
};

interface ProductForm {
  name: string;
  categoryId: string;
  description: string;
  price: string;
  compareAtPrice: string;
  sku: string;
  calories: string;
  weight: string;
  prepTime: string;
  spiciness: string;
  isAvailable: boolean;
  isFeatured: boolean;
  isNew: boolean;
  isVegetarian: boolean;
  isVegan: boolean;
  isGlutenFree: boolean;
  isAlcoholic: boolean;
}

const emptyForm: ProductForm = {
  name: '',
  categoryId: '',
  description: '',
  price: '',
  compareAtPrice: '',
  sku: '',
  calories: '',
  weight: '',
  prepTime: '',
  spiciness: '0',
  isAvailable: true,
  isFeatured: false,
  isNew: false,
  isVegetarian: false,
  isVegan: false,
  isGlutenFree: false,
  isAlcoholic: false,
};

/**
 * Miniatură + acțiuni pentru fotografia produsului.
 *
 * Lista produselor este locul unde se vede cel mai repede ce lipsește: un produs fără fotografie
 * apare cu un chenar punctat și „adaugă foto". Fișierul se urcă prin serviciul de media
 * (`POST /assets`), care verifică tipul real al conținutului, și se atașează produsului.
 */
function ProductThumb({
  product,
  disabled,
  onPick,
  onRemove,
}: {
  product: Product;
  disabled: boolean;
  onPick: (file: File) => void;
  onRemove: (mediaId: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const primary = product.images?.find((image) => image.role === 'primary') ?? product.images?.[0] ?? null;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
        title={primary ? 'Schimbă fotografia' : 'Adaugă o fotografie'}
        className={cx(
          'group h-14 w-14 overflow-hidden rounded-lg border transition-ui',
          primary ? 'border-ink-100' : 'border-dashed border-ink-300 hover:border-brand-400',
          disabled ? 'opacity-60' : '',
        )}
      >
        {primary ? (
          <img
            // `publicId` este tokenul public al fișierului; `assetId` este identificatorul intern.
            src={`/api/v1/public/assets/${primary.publicId}`}
            alt={primary.alt ?? product.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-ink-400">
            <Icon name="image" size={16} />
            <span className="text-[9px] leading-none">foto</span>
          </span>
        )}
      </button>
      {primary && !disabled ? (
        <button
          type="button"
          onClick={() => onRemove(primary.id)}
          title="Scoate fotografia"
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-ink-100 bg-surface-1 text-ink-500 transition-ui hover:text-danger"
        >
          <Icon name="close" size={11} />
        </button>
      ) : null}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onPick(file);
          event.target.value = '';
        }}
      />
    </div>
  );
}

export function ProductsPage() {
  const session = useSession();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState<ProductForm>(emptyForm);
  /**
   * Imaginea aleasă în formular, încă neurcată.
   *
   * De ce se ține separat: la creare, produsul nu există încă, deci nu are la ce să se atașeze
   * fișierul. Îl urcăm și îl atașăm imediat după salvare, în aceeași operație din perspectiva
   * utilizatorului — ca să nu existe un pas intermediar în care produsul apare fără poză.
   */
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const imageInput = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(0);
  const [menuIds, setMenuIds] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');

  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);
  const categories = useResource<Category[]>(() => api.get<Category[]>('/categories'), [session.activeAccountId]);
  const menus = useResource<CatalogMenu[]>(() => api.get<CatalogMenu[]>('/menus'), [session.activeAccountId]);

  const pageSize = 50;
  const filterKey = `${search}|${categoryFilter}|${availabilityFilter}`;
  const [pageFilter, setPageFilter] = useState(filterKey);
  if (pageFilter !== filterKey) {
    setPageFilter(filterKey);
    setPage(0);
  }
  const offset = pageFilter === filterKey ? page * pageSize : 0;

  const query = new URLSearchParams({ limit: String(pageSize), offset: String(offset) });
  if (search.trim()) query.set('search', search.trim());
  if (categoryFilter) query.set('categoryId', categoryFilter);
  if (availabilityFilter) query.set('available', availabilityFilter);

  const products = useResource<{
    data: Product[];
    meta: { total: number; unavailable: number; featured: number; averagePriceCents: number; limit: number; offset: number };
  }>(
    () =>
      api.getRaw(`/products?${query.toString()}`),
    [search, categoryFilter, availabilityFilter, offset, session.activeAccountId],
  );

  const list = products.data?.data ?? [];
  const total = products.data?.meta.total ?? 0;
  const stats = {
    total,
    unavailable: products.data?.meta.unavailable ?? 0,
    featured: products.data?.meta.featured ?? 0,
    average: products.data?.meta.averagePriceCents ?? 0,
  };

  const canCreate = session.can('product.create');
  const canUpdate = session.can('product.update');
  const canDelete = session.can('product.delete');
  const menuRestaurantId =
    editing?.restaurant_id ||
    categories.data?.find((category) => category.id === form.categoryId)?.restaurant_id ||
    restaurants.data?.[0]?.id ||
    '';
  const restaurantMenus = (menus.data ?? []).filter((menu) => menu.restaurant_id === menuRestaurantId);

  async function addCategory() {
    const name = newCategory.trim();
    const restaurantId = menuRestaurantId;
    if (name.length < 2 || !restaurantId) return;
    setBusy(true);
    try {
      const created = await api.post<{ id: string }>('/categories', { restaurantId, name });
      await categories.reload();
      setForm((current) => ({ ...current, categoryId: created.id }));
      setNewCategory('');
      toast.success('Categoria a fost adăugată', 'O poți alege imediat pentru acest produs.');
    } catch (error) {
      toast.error('Categoria nu a putut fi creată', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function openCreate() {
    setForm({ ...emptyForm, categoryId: categories.data?.[0]?.id ?? '' });
    setMenuIds([]);
    setNewCategory('');
    setErrors({});
    setCreateOpen(true);
  }

  function openEdit(product: Product) {
    setForm({
      name: product.name,
      categoryId: product.category_id ?? '',
      description: product.description ?? '',
      price: (product.price_cents / 100).toFixed(2),
      compareAtPrice: product.compare_at_price_cents ? (product.compare_at_price_cents / 100).toFixed(2) : '',
      sku: product.sku ?? '',
      calories: product.calories_kcal ? String(product.calories_kcal) : '',
      weight: product.weight_grams ? String(product.weight_grams) : '',
      prepTime: product.prep_time_minutes ? String(product.prep_time_minutes) : '',
      spiciness: String(product.spiciness ?? 0),
      isAvailable: product.is_available,
      isFeatured: product.is_featured,
      isNew: product.is_new,
      isVegetarian: product.is_vegetarian,
      isVegan: product.is_vegan,
      isGlutenFree: product.is_gluten_free,
      isAlcoholic: product.is_alcoholic,
    });
    setErrors({});
    setPendingImage(null);
    setNewCategory('');
    setMenuIds([]);
    setEditing(product);
    void api
      .get<{ menuIds: string[] }>(`/products/${product.id}/menus`)
      .then((placement) => setMenuIds(placement.menuIds))
      .catch(() => setMenuIds([]));
  }

  /** Construim payload-ul doar cu valorile completate, ca „gol” să însemne „șterge valoarea”. */
  function payload() {
    const priceCents = Math.round(Number.parseFloat(form.price.replace(',', '.')) * 100);
    return {
      name: form.name.trim(),
      categoryId: form.categoryId || null,
      description: form.description.trim() || undefined,
      priceCents: Number.isFinite(priceCents) ? priceCents : 0,
      compareAtPriceCents: form.compareAtPrice
        ? Math.round(Number.parseFloat(form.compareAtPrice.replace(',', '.')) * 100)
        : null,
      sku: form.sku.trim() || undefined,
      caloriesKcal: form.calories ? Number.parseInt(form.calories, 10) : null,
      weightGrams: form.weight ? Number.parseInt(form.weight, 10) : null,
      prepTimeMinutes: form.prepTime ? Number.parseInt(form.prepTime, 10) : null,
      spiciness: Number.parseInt(form.spiciness, 10) || 0,
      isAvailable: form.isAvailable,
      isFeatured: form.isFeatured,
      isNew: form.isNew,
      isVegetarian: form.isVegetarian,
      isVegan: form.isVegan,
      isGlutenFree: form.isGlutenFree,
      isAlcoholic: form.isAlcoholic,
    };
  }

  async function save() {
    setBusy(true);
    setErrors({});
    try {
      let productId = editing?.id ?? null;
      if (editing) {
        await api.patch(`/products/${editing.id}`, payload());
      } else {
        const restaurantId = categories.data?.find((c) => c.id === form.categoryId)?.restaurant_id ?? restaurants.data?.[0]?.id;
        // `api.post` întoarce deja conținutul util (serverul trimite `{ data: … }`, clientul despachetează).
        const created = await api.post<{ id: string }>('/products', { ...payload(), restaurantId });
        productId = created.id;
      }

      // Imaginea aleasă în formular devine imaginea principală a produsului.
      if (pendingImage && productId) {
        await uploadProductImage(productId, pendingImage, form.name);
      }
      if (productId) {
        await api.put(`/products/${productId}/menus`, { menuIds });
      }

      setPendingImage(null);
      setCreateOpen(false);
      setEditing(null);
      await products.reload();
      await categories.reload();
      await menus.reload();
      toast.success(
        editing ? 'Produsul a fost actualizat' : 'Produsul a fost adăugat',
        menuIds.length
          ? 'Este în meniurile bifate. Meniurile deja publicate s-au actualizat.'
          : 'Nu este încă într-un meniu. Bifează meniurile la editare, apoi salvează.',
      );
    } catch (error) {
      const fields = fieldErrors(error);
      setErrors(fields);
      toast.error('Produsul nu a putut fi salvat', Object.keys(fields).length ? Object.values(fields)[0]?.[0] : errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  /** Urcă fișierul (serviciul de media validează tipul din conținut) și îl atașează produsului. */
  async function uploadProductImage(productId: string, file: File, alt: string) {
    const created = await uploadFile<{ data: { id: string } }>(
      `/assets?filename=${encodeURIComponent(file.name)}&alt=${encodeURIComponent(alt)}`,
      file,
    );
    await api.post(`/products/${productId}/media`, { assetId: created.data.id, role: 'primary', alt });
  }

  /** Atașare directă din listă (produs care are deja poză în bibliotecă sau poză nouă). */
  async function attachImageFor(product: Product, file: File) {
    setImageBusy(true);
    try {
      await uploadProductImage(product.id, file, product.name);
      await products.reload();
      toast.success(`Fotografie adăugată pentru „${product.name}”`, 'Se vede în meniul public după următoarea publicare.');
    } catch (error) {
      toast.error('Fotografia nu a putut fi adăugată', errorMessage(error));
    } finally {
      setImageBusy(false);
    }
  }

  async function removeImage(product: Product, mediaId: string) {
    setImageBusy(true);
    try {
      await api.del(`/products/${product.id}/media/${mediaId}`);
      await products.reload();
      toast.success(`Fotografia a fost scoasă de la „${product.name}”`, 'Fișierul rămâne în bibliotecă.');
    } catch (error) {
      toast.error('Fotografia nu a putut fi scoasă', errorMessage(error));
    } finally {
      setImageBusy(false);
    }
  }

  async function toggleAvailability(product: Product) {
    // Optimist: în timpul serviciului, comutarea trebuie să fie instantă.
    const next = !product.is_available;
    products.setData((current) =>
      current
        ? {
            ...current,
            data: current.data.map((item) => (item.id === product.id ? { ...item, is_available: next } : item)),
            meta: {
              ...current.meta,
              unavailable: current.meta.unavailable + (next ? -1 : 1),
            },
          }
        : current,
    );
    try {
      await api.post(`/products/${product.id}/availability`, { isAvailable: next });
      toast.success(next ? `„${product.name}” este din nou disponibil` : `„${product.name}” a fost marcat ca indisponibil`);
    } catch (error) {
      products.setData((current) =>
        current
          ? {
              ...current,
              data: current.data.map((item) => (item.id === product.id ? { ...item, is_available: !next } : item)),
              meta: {
                ...current.meta,
                unavailable: current.meta.unavailable + (next ? 1 : -1),
              },
            }
          : current,
      );
      toast.error('Starea nu a putut fi schimbată', errorMessage(error));
    }
  }

  async function remove() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/products/${deleting.id}`);
      toast.success(`„${deleting.name}” a fost șters din catalog`);
      setDeleting(null);
      await products.reload();
      await categories.reload();
    } catch (error) {
      toast.error('Produsul nu a putut fi șters', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Produse"
        subtitle="Catalogul central — o singură dată aici, apoi în toate canalele: QR, meniu web, ecrane."
        actions={
          canCreate ? (
            <Button icon={<Icon name="plus" size={17} />} onClick={openCreate}>
              Produs nou
            </Button>
          ) : null
        }
      />

      {/* Indicatori rapizi + filtre */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Produse</span>
          <span className="font-display text-[26px] leading-none text-ink-950 nums">{stats.total}</span>
        </Card>
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Indisponibile acum</span>
          <span className={cx('font-display text-[26px] leading-none nums', stats.unavailable ? 'text-warning' : 'text-ink-950')}>
            {stats.unavailable}
          </span>
        </Card>
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Recomandate</span>
          <span className="font-display text-[26px] leading-none text-ink-950 nums">{stats.featured}</span>
        </Card>
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Preț mediu</span>
          <span className="font-display text-[26px] leading-none text-ink-950 nums">
            {formatCurrency(stats.average, list[0]?.currency ?? 'RON')}
          </span>
        </Card>
      </div>

      <Card className="mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Icon name="search" size={17} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Caută după nume sau SKU…"
            className="pl-9"
            aria-label="Caută produse"
          />
        </div>
        <Select
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
          className="sm:w-52"
          aria-label="Filtrează după categorie"
        >
          <option value="">Toate categoriile</option>
          {(categories.data ?? []).map((category) => (
            <option key={category.id} value={category.id}>
              {category.name} ({category.products})
            </option>
          ))}
        </Select>
        <Select
          value={availabilityFilter}
          onChange={(event) => setAvailabilityFilter(event.target.value)}
          className="sm:w-44"
          aria-label="Filtrează după disponibilitate"
        >
          <option value="">Toate stările</option>
          <option value="true">Disponibile</option>
          <option value="false">Indisponibile</option>
        </Select>
      </Card>

      {products.loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : products.error ? (
        <InlineAlert tone="warning" title="Produsele nu au putut fi încărcate">
          {products.error} <button type="button" className="underline" onClick={() => void products.reload()}>Reîncearcă</button>
        </InlineAlert>
      ) : !list.length ? (
        <EmptyState
          icon={<Icon name="tag" size={26} />}
          title={search || categoryFilter ? 'Niciun produs pentru filtrele curente' : 'Catalogul este gol'}
          description={
            search || categoryFilter
              ? 'Încearcă alt termen de căutare sau resetează filtrele.'
              : 'Adaugă primul produs: nume, preț și categorie sunt suficiente pentru a începe.'
          }
          action={canCreate && !search && !categoryFilter ? <Button onClick={openCreate}>Adaugă primul produs</Button> : null}
        />
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-ink-100">
            {list.map((product) => (
              <li key={product.id} className="flex flex-col gap-3 p-4 transition-ui hover:bg-surface-2 sm:flex-row sm:items-center">
                <ProductThumb
                  product={product}
                  disabled={imageBusy || !canUpdate}
                  onPick={(file) => void attachImageFor(product, file)}
                  onRemove={(mediaId) => void removeImage(product, mediaId)}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cx('font-medium', product.is_available ? 'text-ink-950' : 'text-ink-400 line-through')}>
                      {product.name}
                    </span>
                    {product.is_featured ? <Badge tone="brand">recomandat</Badge> : null}
                    {product.is_new ? <Badge tone="info">nou</Badge> : null}
                    {product.is_vegetarian ? <Badge tone="success">vegetarian</Badge> : null}
                    {product.is_vegan ? <Badge tone="success">vegan</Badge> : null}
                    {product.is_gluten_free ? <Badge tone="neutral">fără gluten</Badge> : null}
                    {product.spiciness >= 2 ? <Badge tone="warning">picant</Badge> : null}
                    {!product.is_available ? <Badge tone="danger">indisponibil</Badge> : null}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-ink-400">
                    <span>{product.category_name ?? 'fără categorie'}</span>
                    <span>{product.restaurant_name}</span>
                    {product.sku ? <span className="font-mono">{product.sku}</span> : null}
                    {product.weight_grams ? <span className="nums">{product.weight_grams} g</span> : null}
                    {product.calories_kcal ? <span className="nums">{product.calories_kcal} kcal</span> : null}
                    {product.prep_time_minutes ? <span className="nums">{product.prep_time_minutes} min</span> : null}
                  </div>
                </div>

                {/*
                  * Pe ecran mic, rândul cu preț și cele trei butoane (disponibil / Editează / Șterge)
                  * are nevoie de ~400 px: fără `flex-wrap`, împingea cardul la 451 px pe un ecran de
                  * 390 px, iar pagina căpăta derulare laterală. Se repliază, nu se înghesuie.
                  */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="flex flex-col items-end">
                    {product.compare_at_price_cents ? (
                      <span className="text-caption text-ink-400 line-through nums">
                        {formatCurrency(product.compare_at_price_cents, product.currency)}
                      </span>
                    ) : null}
                    <span className="font-display text-[20px] leading-none text-ink-950 nums">
                      {formatCurrency(product.price_cents, product.currency)}
                    </span>
                  </div>

                  {canUpdate ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void toggleAvailability(product)}
                        title={product.is_available ? 'Marchează ca indisponibil' : 'Marchează ca disponibil'}
                        className={cx(
                          'transition-ui rounded-full px-3 py-1.5 text-caption font-medium',
                          product.is_available
                            ? 'bg-success-bg text-success hover:bg-success/15'
                            : 'bg-danger-bg text-danger hover:bg-danger/15',
                        )}
                      >
                        {product.is_available ? 'disponibil' : 'indisponibil'}
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(product)}>
                        Editează
                      </Button>
                      {canDelete ? (
                        <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDeleting(product)}>
                          Șterge
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          {total > pageSize ? (
            <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-4 py-3">
              <span className="text-caption text-ink-400 nums">
                {offset + 1}–{Math.min(offset + list.length, total)} din {total}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>
                  Înapoi
                </Button>
                <Button size="sm" variant="secondary" disabled={offset + pageSize >= total} onClick={() => setPage((value) => value + 1)}>
                  Înainte
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      )}

      {/* Creare / editare */}
      <Modal
        open={createOpen || Boolean(editing)}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        title={editing ? `Editează „${editing.name}”` : 'Produs nou'}
        description="Prețul și disponibilitatea se reflectă în toate canalele după publicarea meniului."
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setCreateOpen(false);
                setEditing(null);
              }}
            >
              Anulează
            </Button>
            <Button loading={busy} onClick={() => void save()} disabled={form.name.trim().length < 2 || !form.price}>
              {editing ? 'Salvează modificările' : 'Adaugă produsul'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Fotografia: se poate alege și aici, ca produsul nou să aibă poză din prima secundă. */}
            <Field label="Fotografie" hint="PNG, JPEG sau WebP, până la 5 MB. Apare în meniul public și pe ecrane.">
              {() => (
                <div className="flex items-center gap-3">
                  {editing?.images?.length ? (
                    <img
                      src={`/api/v1/public/assets/${(editing.images.find((image) => image.role === 'primary') ?? editing.images[0]!).publicId}`}
                      alt={editing.name}
                      className="h-12 w-12 rounded-lg border border-ink-100 object-cover"
                    />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-ink-300 text-ink-400">
                      <Icon name="image" size={16} />
                    </span>
                  )}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => imageInput.current?.click()}>
                        {editing?.images?.length ? 'Schimbă poza' : 'Alege o poză'}
                      </Button>
                      {pendingImage ? (
                        <button
                          type="button"
                          className="text-caption text-ink-500 hover:text-danger"
                          onClick={() => setPendingImage(null)}
                        >
                          renunță
                        </button>
                      ) : null}
                    </div>
                    <span className="text-caption text-ink-400">
                      {pendingImage
                        ? `Se încarcă la salvare: ${pendingImage.name}`
                        : editing?.images?.length
                          ? 'Poza actuală. Înlocuirea se aplică la salvare.'
                          : 'Fără fotografie — produsul apare doar cu text în meniu.'}
                    </span>
                    <input
                      ref={imageInput}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) setPendingImage(file);
                        event.target.value = '';
                      }}
                    />
                  </div>
                </div>
              )}
            </Field>
            <Field label="Nume produs" required errors={errors.name}>
              {(props) => (
                <Input
                  {...props}
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  placeholder="Pizza Diavola"
                />
              )}
            </Field>
            <Field label="Categorie">
              {(props) => (
                <Select {...props} value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}>
                  <option value="">Fără categorie</option>
                  {(categories.data ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-body-s font-medium text-ink-800">Categorie nouă</span>
              <div className="flex gap-2">
                <Input
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                  placeholder="Ex. Pizza, Paste, Desert"
                  aria-label="Numele categoriei noi"
                />
                <Button type="button" variant="secondary" disabled={newCategory.trim().length < 2 || busy} onClick={() => void addCategory()}>
                  Adaugă
                </Button>
              </div>
            </div>
            <fieldset className="flex flex-col gap-2 sm:col-span-2">
              <legend className="text-body-s font-medium text-ink-800">Meniuri</legend>
              <p className="text-caption text-ink-400">
                Bifează meniurile în care apare produsul. Intră în secțiunea categoriei lui. Un meniu nou se creează din pagina Meniuri.
              </p>
              {restaurantMenus.length ? (
                <ul className="flex flex-col gap-2">
                  {restaurantMenus.map((menu) => (
                    <li key={menu.id}>
                      <label className="flex items-start gap-2 text-body-s text-ink-800">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-ink-200"
                          checked={menuIds.includes(menu.id)}
                          onChange={(event) =>
                            setMenuIds((current) =>
                              event.target.checked ? [...current, menu.id] : current.filter((id) => id !== menu.id),
                            )
                          }
                        />
                        <span>
                          {menu.name}
                          <span className="block text-caption text-ink-400">
                            {MENU_MODE[menu.ordering_mode] ?? menu.ordering_mode}
                            {menu.status === 'published' ? ' · publicat' : ' · nepublicat'}
                            {` · ${menu.items} produse`}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-caption text-ink-400">Niciun meniu încă. Creează unul din pagina Meniuri, apoi revino aici și bifează-l.</p>
              )}
            </fieldset>
            <Field label="Preț" hint={restaurants.data?.[0]?.currency ?? 'RON'} required errors={errors.priceCents}>
              {(props) => (
                <Input
                  {...props}
                  value={form.price}
                  onChange={(event) => setForm({ ...form, price: event.target.value })}
                  inputMode="decimal"
                  placeholder="39.00"
                  className="nums"
                />
              )}
            </Field>
            <Field label="Preț vechi" hint="Opțional — apare tăiat, pentru promoții.">
              {(props) => (
                <Input
                  {...props}
                  value={form.compareAtPrice}
                  onChange={(event) => setForm({ ...form, compareAtPrice: event.target.value })}
                  inputMode="decimal"
                  placeholder="45.00"
                  className="nums"
                />
              )}
            </Field>
          </div>

          <Field label="Descriere" hint="Maxim 2 rânduri pe meniul public." errors={errors.description}>
            {(props) => (
              <Textarea
                {...props}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                rows={2}
                placeholder="Ragu de vită, mozzarella, ardei iute, busuioc"
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-4">
            <Field label="SKU">
              {(props) => <Input {...props} value={form.sku} onChange={(event) => setForm({ ...form, sku: event.target.value })} className="font-mono" />}
            </Field>
            <Field label="Gramaj (g)">
              {(props) => <Input {...props} value={form.weight} onChange={(event) => setForm({ ...form, weight: event.target.value })} inputMode="numeric" className="nums" />}
            </Field>
            <Field label="Calorii (kcal)">
              {(props) => <Input {...props} value={form.calories} onChange={(event) => setForm({ ...form, calories: event.target.value })} inputMode="numeric" className="nums" />}
            </Field>
            <Field label="Timp preparare (min)">
              {(props) => <Input {...props} value={form.prepTime} onChange={(event) => setForm({ ...form, prepTime: event.target.value })} inputMode="numeric" className="nums" />}
            </Field>
          </div>

          <Field label="Nivel de picant">
            {(props) => (
              <Select {...props} value={form.spiciness} onChange={(event) => setForm({ ...form, spiciness: event.target.value })}>
                <option value="0">Deloc</option>
                <option value="1">Ușor</option>
                <option value="2">Mediu</option>
                <option value="3">Foarte picant</option>
              </Select>
            )}
          </Field>

          <div className="grid gap-4 border-t border-ink-100 pt-4 sm:grid-cols-2">
            <Switch checked={form.isAvailable} onChange={(value) => setForm({ ...form, isAvailable: value })} label="Disponibil" description="Apare pe meniul public" />
            <Switch checked={form.isFeatured} onChange={(value) => setForm({ ...form, isFeatured: value })} label="Recomandat" description="Evidențiat în meniu și pe ecrane" />
            <Switch checked={form.isNew} onChange={(value) => setForm({ ...form, isNew: value })} label="Nou în meniu" />
            <Switch checked={form.isVegetarian} onChange={(value) => setForm({ ...form, isVegetarian: value })} label="Vegetarian" />
            <Switch checked={form.isVegan} onChange={(value) => setForm({ ...form, isVegan: value })} label="Vegan" />
            <Switch checked={form.isGlutenFree} onChange={(value) => setForm({ ...form, isGlutenFree: value })} label="Fără gluten" />
            <Switch checked={form.isAlcoholic} onChange={(value) => setForm({ ...form, isAlcoholic: value })} label="Conține alcool" />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Ștergi produsul?"
        description={`„${deleting?.name ?? ''}” dispare din meniuri și de pe ecrane. Istoricul vânzărilor rămâne intact.`}
        confirmLabel="Șterge produsul"
        tone="danger"
        loading={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
      />
    </div>
  );
}
